/**
 * Family B — Operator / Proof Surface Separation V0
 * Proof/debug/audit must not appear on operator default surfaces.
 */

export type OperatorSurfaceClassV0 = 'operator_default' | 'proof' | 'diagnostics' | 'audit';

export type ProofLeakRelocationTargetV0 =
  | 'developer_diagnostics'
  | 'audit_drawer'
  | 'evidence_route'
  | 'url_query_gated_proof_mode';

/** Strings / markers forbidden on operator_default (must relocate). */
export const OPERATOR_DEFAULT_PROOF_LEAK_PATTERNS_V0 = [
  'grantsAuthority=false',
  'grantsAuthority=',
  'SYNTHETIC_PROOF_FIXTURE',
  'SYNTHETIC_FIXTURE',
  'Local synthetic authoring',
  'cross-tenant',
  'CROSS_TENANT',
  'fixture_tenant',
  'Fixture tenants only',
  'runtime contract',
  'proof depth',
  'PROOF_ONLY',
  'data-grants-authority',
  'data-testid="ai-orch-grants-authority"',
] as const;

export type ProofLeakPatternV0 = (typeof OPERATOR_DEFAULT_PROOF_LEAK_PATTERNS_V0)[number];

export interface ProofLeakRelocationV0 {
  pattern: ProofLeakPatternV0 | string;
  relocateTo: ProofLeakRelocationTargetV0;
  reason: string;
}

export const DEFAULT_PROOF_LEAK_RELOCATIONS_V0: readonly ProofLeakRelocationV0[] = [
  {
    pattern: 'grantsAuthority=false',
    relocateTo: 'url_query_gated_proof_mode',
    reason: 'Authority denial is a contract fact, not operator chrome',
  },
  {
    pattern: 'Local synthetic authoring',
    relocateTo: 'developer_diagnostics',
    reason: 'Synthetic authoring labels confuse real operators',
  },
  {
    pattern: 'SYNTHETIC_PROOF_FIXTURE',
    relocateTo: 'developer_diagnostics',
    reason: 'Fixture identifiers belong in diagnostics',
  },
  {
    pattern: 'cross-tenant',
    relocateTo: 'evidence_route',
    reason: 'Cross-tenant proof results are evidence, not task UI',
  },
  {
    pattern: 'runtime contract',
    relocateTo: 'audit_drawer',
    reason: 'Runtime contract diagnostics belong in audit',
  },
  {
    pattern: 'proof depth',
    relocateTo: 'evidence_route',
    reason: 'Proof depth markers are not operator work',
  },
];

/** Query keys that enable proof/diagnostics chrome. */
export const OPERATOR_PROOF_MODE_QUERY_KEYS_V0 = ['proof', 'diagnostics', 'audit'] as const;

export function isOperatorProofModeFromSearchV0(search: string): boolean {
  const q = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(q);
  return OPERATOR_PROOF_MODE_QUERY_KEYS_V0.some((key) => {
    const v = params.get(key);
    return v === '1' || v === 'true' || v === 'yes';
  });
}

export function resolveOperatorSurfaceClassV0(input: {
  proofMode: boolean;
  routeKind?: 'evidence' | 'system_proof' | 'operator';
}): OperatorSurfaceClassV0 {
  if (input.routeKind === 'evidence') return 'proof';
  if (input.routeKind === 'system_proof') return 'diagnostics';
  if (input.proofMode) return 'audit';
  return 'operator_default';
}

export function assertNoProofLeakageOnOperatorDefaultV0(
  surfaceClass: OperatorSurfaceClassV0,
  visibleText: string
): { ok: boolean; leaks: string[] } {
  if (surfaceClass !== 'operator_default') {
    return { ok: true, leaks: [] };
  }
  const leaks = OPERATOR_DEFAULT_PROOF_LEAK_PATTERNS_V0.filter((p) => visibleText.includes(p));
  return { ok: leaks.length === 0, leaks: [...leaks] };
}
